/**
 * @fileoverview Hook for managing tareas en proceso (tasks in progress)
 * Provides functionality to fetch and filter production tasks that are currently active
 */

import { useState, useCallback, useRef } from 'react';
import { pb } from '../helpers/pocketbase_helper';
import { 
  TareaOrden, 
  TAREA_ORDEN_COLLECTION 
} from '../types/collections/TareaOrden';
import { 
  LogPlanCentroCosto,
  LOG_PLAN_CENTRO_COSTO_COLLECTION 
} from '../types/collections/LogPlanCentroCosto';
import { 
  OrdenProduccion,
  ORDEN_PRODUCCION_COLLECTION 
} from '../types/collections/OrdenProduccion';
import { 
  PlanCentroCosto,
  PLAN_CENTRO_COSTO_COLLECTION 
} from '../types/collections/PlanCentroCosto';
import { 
  processCompleteTaskProgression,
  calculateTaskProgress,
  activateNextTaskInPlan,
  finalizeTaskOrder
} from '../logic/taskProgression';
import { 
  updateTaskPriority,
  getTasksByPriority,
  resetPrioritiesToDefault
} from '../logic/userPriorityManagement';
import { LogPlanCentroCostoRepository } from '../repositories/LogPlanCentroCostoRepository';
import { TareaOrdenRepository } from '../repositories/TareaOrdenRepository';

/**
 * Interface for tarea with expanded data
 */
export interface TareaOrdenExpanded extends TareaOrden {
  expand?: {
    orden_produccion?: OrdenProduccion;
    plan?: PlanCentroCosto;
    log_plan?: LogPlanCentroCosto;
  };
}

/**
 * Interface for tarea with progress information
 */
export interface TareaConProgreso extends TareaOrdenExpanded {
  progreso: {
    total: number;
    completados: number;
    pendientes: number;
    porcentaje: number;
  };
  ordenInfo?: {
    id?: string;
    numero?: number;
    nombre_producto?: string;
    cliente?: string;
  };
  planInfo?: {
    nombre?: string;
    centro_costo?: string[];
  };
}

/**
 * Hook for managing tareas en proceso operations
 * @returns Object with state and functions for tareas en proceso
 */
export const useTareasEnProceso = () => {
  // State management
  const [tareas, setTareas] = useState<TareaConProgreso[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  
  // AbortController for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError("");
  }, []);

  /**
   * Set loading state
   */
  const setLoadingState = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  /**
   * Set error state
   */
  const setErrorState = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  /**
   * Cancel ongoing requests
   */
  const cancelRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Check if a tarea has pending logs (logs without fin date)
   */
  const hasPendingLogs = useCallback(async (tareaId: string): Promise<boolean> => {
    try {
      const logRepository = new LogPlanCentroCostoRepository();
      const logs = await logRepository.getTasksForOrder(tareaId);

      // Check if any log doesn't have a fin date
      return logs.some(log => !log.fin);
    } catch (err: any) {
      console.error("Error checking pending logs:", err);
      return false;
    }
  }, []);

  /**
   * Get progress information for a tarea
   */
  const getTareaProgress = useCallback(async (tareaId: string): Promise<{
    total: number;
    completados: number;
    pendientes: number;
    porcentaje: number;
  }> => {
    try {
      const logRepository = new LogPlanCentroCostoRepository();
      const logs = await logRepository.getTasksForOrder(tareaId);

      const total = logs.length;
      const completados = logs.filter(log => log.fin).length;
      const pendientes = total - completados;
      const porcentaje = total > 0 ? Math.round((completados / total) * 100) : 0;

      return { total, completados, pendientes, porcentaje };
    } catch (err: any) {
      console.error("Error getting tarea progress:", err);
      return { total: 0, completados: 0, pendientes: 0, porcentaje: 0 };
    }
  }, []);

  /**
   * Fetch tareas en proceso with filtering
   */
  const fetchTareasEnProceso = useCallback(async () => {
    try {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController
      abortControllerRef.current = new AbortController();
      setLoading(true);
      setError("");

      // Use repository to get all tareas from tarea_orden
      const tareaOrdenRepository = new TareaOrdenRepository();
      const tareasResult = await tareaOrdenRepository.getList(1, 1000, {
        filter: 'fin = null', // Only active tasks
        sort: '-created',
        expand: 'orden_produccion,plan'
      });

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      const tareasConProgreso: TareaConProgreso[] = [];

      // Process each tarea to check if it has pending logs
      for (const tarea of tareasResult.items) {
        const hasPending = await hasPendingLogs(tarea.orden_produccion);
        
        if (hasPending) {
          const progreso = await getTareaProgress(tarea.orden_produccion);
          
          const tareaConProgreso: TareaConProgreso = {
            ...tarea as unknown as TareaOrdenExpanded,
            progreso,
            ordenInfo: {
              id: (tarea.expand?.orden_produccion as any)?.id,
              numero: (tarea.expand?.orden_produccion as any)?.numero,
              nombre_producto: (tarea.expand?.orden_produccion as any)?.nombre_producto,
              cliente: (tarea.expand?.orden_produccion as any)?.cliente
            },
            planInfo: {
              nombre: (tarea.expand?.plan as any)?.nombre,
              centro_costo: (tarea.expand?.plan as any)?.centro_costo
            }
          };

          tareasConProgreso.push(tareaConProgreso);
        }
      }

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      setTareas(tareasConProgreso);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // Request was cancelled, don't set error
        return;
      }
      
      console.error("Error fetching tareas en proceso:", err);
      setError("Error al cargar tareas en proceso");
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [hasPendingLogs, getTareaProgress]);

  /**
   * Get a specific tarea by ID
   */
  const getTareaById = useCallback(async (id: string): Promise<TareaConProgreso | null> => {
    try {
      const tareaOrdenRepository = new TareaOrdenRepository();
      const tarea = await tareaOrdenRepository.getById(id);

      const progreso = await getTareaProgress(tarea.orden_produccion);
      
      const tareaConProgreso: TareaConProgreso = {
        ...tarea as unknown as TareaOrdenExpanded,
        progreso,
        ordenInfo: {
          id: (tarea.expand?.orden_produccion as any)?.id,
          numero: (tarea.expand?.orden_produccion as any)?.numero,
          nombre_producto: (tarea.expand?.orden_produccion as any)?.nombre_producto,
          cliente: (tarea.expand?.orden_produccion as any)?.cliente
        },
        planInfo: {
          nombre: (tarea.expand?.plan as any)?.nombre,
          centro_costo: (tarea.expand?.plan as any)?.centro_costo
        }
      };

      return tareaConProgreso;
    } catch (err: any) {
      console.error("Error getting tarea by ID:", err);
      return null;
    }
  }, [getTareaProgress]);

  /**
   * Get tareas for a specific order
   */
  const getTareasByOrden = useCallback(async (ordenId: string): Promise<TareaConProgreso[]> => {
    try {
      const tareaOrdenRepository = new TareaOrdenRepository();
      const result = await tareaOrdenRepository.getList(1, 50, {
        filter: `orden_produccion = "${ordenId}"`,
        sort: '-created',
        expand: 'orden_produccion,plan'
      });

      const tareasConProgreso: TareaConProgreso[] = [];

      for (const tarea of result.items) {
        const progreso = await getTareaProgress(tarea.orden_produccion);
        
        const tareaConProgreso: TareaConProgreso = {
          ...tarea as unknown as TareaOrdenExpanded,
          progreso,
          ordenInfo: {
            id: (tarea.expand?.orden_produccion as any)?.id,
            numero: (tarea.expand?.orden_produccion as any)?.numero,
            nombre_producto: (tarea.expand?.orden_produccion as any)?.nombre_producto,
            cliente: (tarea.expand?.orden_produccion as any)?.cliente
          },
          planInfo: {
            nombre: (tarea.expand?.plan as any)?.nombre,
            centro_costo: (tarea.expand?.plan as any)?.centro_costo
          }
        };

        tareasConProgreso.push(tareaConProgreso);
      }

      return tareasConProgreso;
    } catch (err: any) {
      console.error("Error getting tareas by order:", err);
      return [];
    }
  }, [getTareaProgress]);

  /**
   * Complete task progression workflow
   * Handles task completion, progress calculation, and next task activation
   */
  const completeTaskProgression = useCallback(async (
    tareaOrdenId: string,
    completedLogId: string,
    ordenId: string,
    completedOrdenPlan: number
  ) => {
    try {
      setLoading(true);
      setError("");

      const result = await processCompleteTaskProgression(
        tareaOrdenId,
        completedLogId,
        ordenId,
        completedOrdenPlan
      );

      // Refresh the tareas list to reflect changes
      await fetchTareasEnProceso();

      return result;
    } catch (err: any) {
      console.error("Error completing task progression:", err);
      setError(`Error al completar progreso de tarea: ${err.message}`);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTareasEnProceso]);

  /**
   * Calculate task progress using new logic
   */
  const calculateTaskProgressNew = useCallback(async (
    tareaOrdenId: string,
    completedLogId: string
  ) => {
    try {
      const result = await calculateTaskProgress(tareaOrdenId, completedLogId);
      
      // Refresh the tareas list to reflect changes
      await fetchTareasEnProceso();

      return result;
    } catch (err: any) {
      console.error("Error calculating task progress:", err);
      setError(`Error al calcular progreso: ${err.message}`);
      throw err;
    }
  }, [fetchTareasEnProceso]);

  /**
   * Activate next task in plan
   */
  const activateNextTask = useCallback(async (
    ordenId: string,
    currentOrdenPlan: number
  ) => {
    try {
      const result = await activateNextTaskInPlan(ordenId, currentOrdenPlan);
      
      // Refresh the tareas list to reflect changes
      await fetchTareasEnProceso();

      return result;
    } catch (err: any) {
      console.error("Error activating next task:", err);
      setError(`Error al activar siguiente tarea: ${err.message}`);
      throw err;
    }
  }, [fetchTareasEnProceso]);

  /**
   * Finalize task order
   */
  const finalizeOrder = useCallback(async (tareaOrdenId: string) => {
    try {
      const result = await finalizeTaskOrder(tareaOrdenId);
      
      // Refresh the tareas list to reflect changes
      await fetchTareasEnProceso();

      return result;
    } catch (err: any) {
      console.error("Error finalizing order:", err);
      setError(`Error al finalizar orden: ${err.message}`);
      throw err;
    }
  }, [fetchTareasEnProceso]);

  /**
   * Update task priority
   */
  const updatePriority = useCallback(async (logId: string, priority: number) => {
    try {
      const result = await updateTaskPriority(logId, priority);
      
      // Refresh the tareas list to reflect changes
      await fetchTareasEnProceso();

      return result;
    } catch (err: any) {
      console.error("Error updating priority:", err);
      setError(`Error al actualizar prioridad: ${err.message}`);
      throw err;
    }
  }, [fetchTareasEnProceso]);

  /**
   * Get tasks by priority
   */
  const getTasksByPriorityForOrder = useCallback(async (ordenId: string) => {
    try {
      return await getTasksByPriority(ordenId);
    } catch (err: any) {
      console.error("Error getting tasks by priority:", err);
      setError(`Error al obtener tareas por prioridad: ${err.message}`);
      throw err;
    }
  }, []);

  /**
   * Reset priorities to default
   */
  const resetPriorities = useCallback(async (ordenId: string) => {
    try {
      const result = await resetPrioritiesToDefault(ordenId);
      
      // Refresh the tareas list to reflect changes
      await fetchTareasEnProceso();

      return result;
    } catch (err: any) {
      console.error("Error resetting priorities:", err);
      setError(`Error al restablecer prioridades: ${err.message}`);
      throw err;
    }
  }, [fetchTareasEnProceso]);

  /**
   * Cleanup function for component unmount
   */
  const cleanup = useCallback(() => {
    cancelRequests();
  }, [cancelRequests]);

  return {
    // State
    tareas,
    loading,
    error,
    
    // Actions
    fetchTareasEnProceso,
    getTareaById,
    getTareasByOrden,
    
    // New Task Progression Functions
    completeTaskProgression,
    calculateTaskProgress: calculateTaskProgressNew,
    activateNextTask,
    finalizeOrder,
    
    // Priority Management Functions
    updatePriority,
    getTasksByPriority: getTasksByPriorityForOrder,
    resetPriorities,
    
    // Utilities
    clearError,
    setLoading: setLoadingState,
    setError: setErrorState,
    cancelRequests,
    cleanup
  };
};
